import React, { useState, useEffect, useMemo, useRef } from "react";
import Paper from "@mui/material/Paper";
import {
  Box,
  Grid,
  CircularProgress,
  Typography,
  TextField,
  Button,
  Stack,
  Snackbar,
  Alert,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import FileDownloadIcon from "@mui/icons-material/FileDownload";

import { getAggregation } from "../../api/aggregationAPI";
import {getProjects} from "../../api/projectAPI";
import { useTranslation } from "react-i18next";
import ConvertOIDValueToString from "../../utils/objectConverter";
import { useLocation } from "react-router-dom";
import { useRefreshInterval } from "../../contexts/RefreshIntervalContext";
import { exportSalesReport } from "../../utils/reportsExporter";
import { usePageVisibility } from "../../hooks/usePageVisibility";
import { getPlaceIdFromPathname } from "../../utils/pathUtils";
import { useObjectData } from "../../hooks/useLocalStorageData";
import { useSnackbar } from "../../hooks/useSnackbar";

function WidgetList({ widget, getAggregation, objectData, nodeId, startEpoch, endEpoch, selectedTimePeriod }) {
  const { t, i18n } = useTranslation();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalSalesAmount, setTotalSalesAmount] = useState(0);
  const [totalSalesCount, setTotalSalesCount] = useState(0);
  const [cashSalesAmount, setCashSalesAmount] = useState(0);
  const [cashSalesCount, setCashSalesCount] = useState(0);
  const [mobileSalesAmount, setMobileSalesAmount] = useState(0);
  const [mobileSalesCount, setMobileSalesCount] = useState(0);
  const [cardSalesAmount, setCardSalesAmount] = useState(0);
  const [cardSalesCount, setCardSalesCount] = useState(0);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 20,
  });
  const { snackbar, showSnackbar, hideSnackbar } = useSnackbar();
  const { interval, isRunning } = useRefreshInterval();
  const isPageVisible = usePageVisibility();
  const intervalMs = useMemo(
    () => Math.max((interval || 15) * 1000, 1000),
    [interval]
  );

  const handleExportReport = () => {
    try {
      const summaryData = {
        totalAmount: totalSalesAmount,
        totalCount: totalSalesCount,
        cardAmount: cardSalesAmount,
        cardCount: cardSalesCount,
        cashAmount: cashSalesAmount,
        cashCount: cashSalesCount,
        mobileAmount: mobileSalesAmount,
        mobileCount: mobileSalesCount,
      };

      const result = exportSalesReport(rows, columns, nodeId, summaryData);

      if (result.success) {
        showSnackbar(
          t('nodeComponents.reports.reportDownloadSuccess', 'Satış raporu başarıyla indirildi: {{fileName}}', { fileName: result.fileName }),
          'success'
        );
      } else {
        showSnackbar(
          result.error || t('nodeComponents.reports.reportCreateError', 'Rapor oluşturulurken bir hata oluştu'),
          'error'
        );
      }
    } catch (error) {
      console.error('Excel export hatası:', error);
      showSnackbar(
        t('nodeComponents.reports.reportDownloadError', 'Rapor indirilemedi'),
        'error'
      );
    }
  };

  const ROW_HEIGHT = 44;
  const HEADER_HEIGHT = 48;

  const columns = useMemo(() => {
    if (!Array.isArray(widget.targetObjectList)) return [];
    const dateColumn = {
      field: "date",
      headerName: t("widgets.date", "Date"),
      flex: 1,
      minWidth: 130,
      sortable: false,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => (
        <Typography
          variant="body2"
          sx={{
            width: "100%",
            textAlign: "center",
            fontSize: "0.7rem",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {params.value ?? "-"}
        </Typography>
      ),
    };

    const lang = (
      localStorage.getItem("i18nextLng") ||
      i18n.language ||
      "en"
    ).split("-")[0].toLowerCase(); // tr-TR -> tr
    const dynamicColumns = widget.targetObjectList
      .filter((c) => c !== "NID" && c !== "NodeName")
      .map((code) => {
        const field = code.toString();
        let headerName = field;
        const numericCode = parseInt(field, 10);
        if (!Number.isNaN(numericCode)) {
          const objInfo = objectData.find((o) => o.code === numericCode);
          if (objInfo) {
            const localizedName =
              lang === "tr"
                ? objInfo.name1 ?? objInfo.name
                : lang === "de"
                ? objInfo.name3 ?? objInfo.name
                : lang === "fr"
                ? objInfo.name4 ?? objInfo.name
                : lang === "es"
                ? objInfo.name5 ?? objInfo.name
                : objInfo.name2 ?? objInfo.name1 ?? objInfo.name;
            if (localizedName) headerName = localizedName;
            else {
              const translationKey = `widgets.${field}`;
              headerName = t(translationKey, { defaultValue: field });
            }
          } else {
            const translationKey = `widgets.${field}`;
            headerName = t(translationKey, { defaultValue: field });
          }
        } else {
          const translationKey = `widgets.${field}`;
          headerName = t(translationKey, { defaultValue: field });
        }
        return {
          field,
          headerName,
          flex: 1,
          minWidth: 90,
          sortable: false,
          headerAlign: "center",
          align: "center",
          renderCell: (params) => {
            const value = params.value;
            return (
              <Typography
                variant="body2"
                sx={{
                  width: "100%",
                  textAlign: "center",
                  fontSize: "0.75rem",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {typeof value === "number"
                  ? value.toLocaleString()
                  : value ?? "-"}
              </Typography>
            );
          },
        };
      });
    return [dateColumn, ...dynamicColumns];
  }, [widget.targetObjectList, t, objectData, i18n.language]);

  const convertValue = useMemo(
    () => (codeStr, rawVal) => {
      if (rawVal === null || rawVal === undefined) return null;
      if (codeStr === "NID") return rawVal;
      const numericCode = parseInt(codeStr, 10);
      if (isNaN(numericCode)) return rawVal;
      const objInfo = objectData.find((o) => o.code === numericCode);
      if (!objInfo) return rawVal;
      if (
        widget.isConvertableObject === false ||
        objInfo.isConvertableObject === false
      ) {
        return rawVal;
      }
      try {
        return ConvertOIDValueToString({ Type: objInfo.type }, rawVal);
      } catch (e) {
        return rawVal;
      }
    },
    [objectData, widget.isConvertableObject]
  );

  useEffect(() => {
    if (!isRunning || !isPageVisible) {
      return;
    }

    let intervalId;
    async function fetchList() {
      setLoading(true);
      setError(null);
      if (!nodeId) {
        setRows([]);
        setLoading(false);
        return;
      }
      try {
        const payloadObj = typeof widget.request.payload === 'string'
          ? JSON.parse(widget.request.payload)
          : widget.request.payload;

        const finalPayload = {
          ...payloadObj,
          start: startEpoch !== null ? startEpoch : payloadObj.start,
          end: endEpoch !== null ? endEpoch : payloadObj.end,
          count: 0,
          timePeriod: selectedTimePeriod !== null
            ? selectedTimePeriod
            : (startEpoch !== null || endEpoch !== null) ? 0 : payloadObj.timePeriod,
        };

        const data = await getAggregation(
          JSON.stringify(finalPayload),
          widget.request.endpoint,
          nodeId
        );
        let records = [];
        if (Array.isArray(data)) {
          records = data;
        } else if (data && Array.isArray(data.rows)) {
          records = data.rows;
        } else if (data && Array.isArray(data.data)) {
          records = data.data;
        } else {
          records = [];
        }
        records.sort((a, b) => {
          const getEpoch = (rec) => {
            let v = rec?.epoch ?? rec?.Epoch ?? rec?.timestamp ?? rec?.time;
            if (v === undefined || v === null || v === "") return -Infinity;
            let num = Number(v);
            if (Number.isNaN(num)) return -Infinity;
            if (num < 1e12) num *= 1000;
            return num;
          };
          return getEpoch(b) - getEpoch(a);
        });
        const getValueFromRecord = (rec, code) => {
          if (!rec || typeof rec !== "object") return null;
          if (Object.prototype.hasOwnProperty.call(rec, code)) {
            return rec[code];
          }
          const numCode = Number(code);
          if (!Number.isNaN(numCode) && Array.isArray(rec.paramList)) {
            const found = rec.paramList.find((p) => Number(p?.oid) === numCode);
            if (found && found.val !== undefined) return found.val;
          }
          return null;
        };

        const coerceNumberIfPossible = (v) => {
          if (v === null || v === undefined) return v;
          if (typeof v === "number") return v;
          if (typeof v === "string" && v.trim() !== "") {
            const n = Number(v);
            if (!Number.isNaN(n)) return n;
          }
          return v;
        };

        // Satışları ödeme tipine göre hesapla
        // 1011: 307 = Nakit, 309 = Mobil, 314 = Kart
        let totalCount = 0;
        let totalAmount = 0;
        let cashCount = 0;
        let cashAmount = 0;
        let mobileCount = 0;
        let mobileAmount = 0;
        let cardCount = 0;
        let cardAmount = 0;

        records.forEach((rec) => {
          const paymentType = getValueFromRecord(rec, "1011");
          const amount = getValueFromRecord(rec, "1004");

          let numAmount = 0;
          if (amount !== null && amount !== undefined) {
            numAmount = Number(amount);
            if (Number.isNaN(numAmount)) {
              numAmount = 0;
            }
          }

          if (paymentType === 307) {
            // Nakit
            cashCount++;
            cashAmount += numAmount;
            totalCount++;
            totalAmount += numAmount;
          } else if (paymentType === 309) {
            // Mobil
            mobileCount++;
            mobileAmount += numAmount;
            totalCount++;
            totalAmount += numAmount;
          } else if (paymentType === 314) {
            // Kart
            cardCount++;
            cardAmount += numAmount;
            totalCount++;
            totalAmount += numAmount;
          }
        });

        setTotalSalesCount(totalCount);
        setTotalSalesAmount(totalAmount);
        setCashSalesCount(cashCount);
        setCashSalesAmount(cashAmount);
        setMobileSalesCount(mobileCount);
        setMobileSalesAmount(mobileAmount);
        setCardSalesCount(cardCount);
        setCardSalesAmount(cardAmount);

        const processed = records.map((rec, idx) => {
          const rawEpoch =
            rec?.epoch ?? rec?.Epoch ?? rec?.timestamp ?? rec?.time;
          const id = rec?._id ?? `${rec?.NID ?? "n"}-${rawEpoch ?? "t"}-${idx}`;
          const row = { id };

          if (rawEpoch !== undefined && rawEpoch !== null && rawEpoch !== "") {
            let epochNum = Number(rawEpoch);
            if (!Number.isNaN(epochNum)) {
              if (epochNum < 1e12) epochNum *= 1000;
              const d = new Date(epochNum);
              const isValid = !isNaN(d.getTime());
              row.date = isValid
                ? d.toLocaleString(undefined, {
                    year: "2-digit",
                    month: "2-digit",
                    day: "2-digit",
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit",
                  })
                : "-";
            } else {
              row.date = "-";
            }
          } else {
            row.date = null;
          }

          if (Array.isArray(widget.targetObjectList)) {
            widget.targetObjectList.forEach((code) => {
              if (code === "NID" || code === "NodeName") return;
              const rawVal = getValueFromRecord(rec, code);
              const maybeNumber = coerceNumberIfPossible(rawVal);
              row[code] = convertValue(code, maybeNumber);
            });
          }
          return row;
        });
        setRows(processed);
      } catch (e) {
        console.error("WidgetList fetch error", e);
        setError(e.message || t('nodeComponents.reports.dataFetchError', 'Veri alınamadı'));
        setRows([]);
      } finally {
        setLoading(false);
      }
    }
    fetchList();
    intervalId = setInterval(fetchList, intervalMs);
    return () => clearInterval(intervalId);
  }, [
    widget,
    getAggregation,
    objectData,
    convertValue,
    nodeId,
    intervalMs,
    isRunning,
    startEpoch,
    endEpoch,
    selectedTimePeriod,
    t,
    isPageVisible,
  ]);

  return (
    <Paper
      elevation={2}
      sx={(theme) => ({
        p: 0.5,
        borderRadius: 0,
        width: "100%",
        display: "flex",
        flexDirection: "column",
        marginBottom: 2,
        backgroundColor: theme.palette.background.paper,
        position: "relative",
        border: `1px solid ${theme.palette.divider}`,
      })}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 1, py: 0.5 }}>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 600,
            textAlign: "center",
            flex: 1,
          }}
          color="text.primary"
        >
          {t("widgets.lastSales", "Satışlar")}
        </Typography>
        <Button
          variant="contained"
          size="small"
          startIcon={<FileDownloadIcon />}
          onClick={handleExportReport}
          sx={{
            textTransform: 'none',
            borderRadius: 1,
            fontWeight: 500,
          }}
        >
          {t('nodeComponents.reports.downloadReport', 'Rapor İndir')}
        </Button>
      </Box>

      <Box
        sx={{
          display: "flex",
          gap: 1,
          px: 2,
          py: 1.5,
          justifyContent: "stretch",
          flexWrap: "wrap",
        }}
      >
        <Paper
          elevation={1}
          sx={(theme) => ({
            flex: 1,
            px: 2,
            py: 2,
            minWidth: 140,
            backgroundColor: theme.palette.mode === "dark" ? "#1e4d2b" : "#e8f5e9",
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 2,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
          })}
        >
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontWeight: 600, display: 'block', mb: 0.5, fontSize: '0.9rem' }}
          >
            {t('vendingSales.totalSalesAmount', 'Toplam Satış Tutarı')}
          </Typography>
          <Typography
            variant="h5"
            sx={{ fontWeight: 700, color: '#2e7d32' }}
          >
            {(totalSalesAmount / 100).toLocaleString('tr-TR', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })} ₺
          </Typography>
        </Paper>

        <Paper
          elevation={1}
          sx={(theme) => ({
            flex: 1,
            px: 2,
            py: 2,
            minWidth: 140,
            backgroundColor: theme.palette.mode === "dark" ? "#1e4d2b" : "#e8f5e9",
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 2,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
          })}
        >
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontWeight: 600, display: 'block', mb: 0.5, fontSize: '0.9rem' }}
          >
            {t('vendingSales.cardSalesAmount', 'Kart Satış Tutarı')}
          </Typography>
          <Typography
            variant="h5"
            sx={{ fontWeight: 700, color: '#2e7d32' }}
          >
            {(cardSalesAmount / 100).toLocaleString('tr-TR', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })} ₺
          </Typography>
        </Paper>

        <Paper
          elevation={1}
          sx={(theme) => ({
            flex: 1,
            px: 2,
            py: 2,
            minWidth: 140,
            backgroundColor: theme.palette.mode === "dark" ? "#1e4d2b" : "#e8f5e9",
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 2,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
          })}
        >
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontWeight: 600, display: 'block', mb: 0.5, fontSize: '0.9rem' }}
          >
            {t('vendingSales.cashSalesAmount', 'Nakit Satış Tutarı')}
          </Typography>
          <Typography
            variant="h5"
            sx={{ fontWeight: 700, color: '#2e7d32' }}
          >
            {(cashSalesAmount / 100).toLocaleString('tr-TR', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })} ₺
          </Typography>
        </Paper>

        <Paper
          elevation={1}
          sx={(theme) => ({
            flex: 1,
            px: 2,
            py: 2,
            minWidth: 140,
            backgroundColor: theme.palette.mode === "dark" ? "#1e4d2b" : "#e8f5e9",
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 2,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
          })}
        >
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontWeight: 600, display: 'block', mb: 0.5, fontSize: '0.9rem' }}
          >
            {t('vendingSales.mobileSalesAmount', 'Mobil Satış Tutarı')}
          </Typography>
          <Typography
            variant="h5"
            sx={{ fontWeight: 700, color: '#2e7d32' }}
          >
            {(mobileSalesAmount / 100).toLocaleString('tr-TR', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })} ₺
          </Typography>
        </Paper>

        <Paper
          elevation={1}
          sx={(theme) => ({
            flex: 1,
            px: 2,
            py: 2,
            minWidth: 140,
            backgroundColor: theme.palette.mode === "dark" ? "#1a237e" : "#e3f2fd",
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 2,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
          })}
        >
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontWeight: 600, display: 'block', mb: 0.5, fontSize: '0.9rem' }}
          >
            {t('vendingSales.totalSalesCount', 'Toplam Satış Adedi')}
          </Typography>
          <Typography
            variant="h5"
            sx={{ fontWeight: 700, color: '#1565c0' }}
          >
            {totalSalesCount.toLocaleString()}
          </Typography>
        </Paper>

        <Paper
          elevation={1}
          sx={(theme) => ({
            flex: 1,
            px: 2,
            py: 2,
            minWidth: 140,
            backgroundColor: theme.palette.mode === "dark" ? "#1a237e" : "#e3f2fd",
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 2,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
          })}
        >
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontWeight: 600, display: 'block', mb: 0.5, fontSize: '0.9rem' }}
          >
            {t('vendingSales.cardSalesCount', 'Kart Satış Adedi')}
          </Typography>
          <Typography
            variant="h5"
            sx={{ fontWeight: 700, color: '#1565c0' }}
          >
            {cardSalesCount.toLocaleString()}
          </Typography>
        </Paper>

        <Paper
          elevation={1}
          sx={(theme) => ({
            flex: 1,
            px: 2,
            py: 2,
            minWidth: 140,
            backgroundColor: theme.palette.mode === "dark" ? "#1a237e" : "#e3f2fd",
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 2,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
          })}
        >
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontWeight: 600, display: 'block', mb: 0.5, fontSize: '0.9rem' }}
          >
            {t('vendingSales.cashSalesCount', 'Nakit Satış Adedi')}
          </Typography>
          <Typography
            variant="h5"
            sx={{ fontWeight: 700, color: '#1565c0' }}
          >
            {cashSalesCount.toLocaleString()}
          </Typography>
        </Paper>

        <Paper
          elevation={1}
          sx={(theme) => ({
            flex: 1,
            px: 2,
            py: 2,
            minWidth: 140,
            backgroundColor: theme.palette.mode === "dark" ? "#1a237e" : "#e3f2fd",
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 2,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
          })}
        >
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontWeight: 600, display: 'block', mb: 0.5, fontSize: '0.9rem' }}
          >
            {t('vendingSales.mobileSalesCount', 'Mobil Satış Adedi')}
          </Typography>
          <Typography
            variant="h5"
            sx={{ fontWeight: 700, color: '#1565c0' }}
          >
            {mobileSalesCount.toLocaleString()}
          </Typography>
        </Paper>
      </Box>
      <Box
        sx={{
          flex: 1,
          height: "auto",
        }}
      >
        <DataGrid
          rows={rows}
          columns={columns}
          pagination
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[20]}
          density="compact"
          loading={loading}
          rowHeight={ROW_HEIGHT}
          headerHeight={HEADER_HEIGHT}
          sx={{
            height: "100%",
            overflow: "unset",
            border: "none",
            "& .MuiDataGrid-main": {
              height: "100%",
            },
            "& .MuiDataGrid-virtualScroller": {
              height: "100%",
              overflowY: "auto",
              overflowX: "hidden",
            },
            "& .MuiDataGrid-columnHeaders": {
              color: (theme) => theme.palette.text.primary,
              minHeight: HEADER_HEIGHT,
              maxHeight: HEADER_HEIGHT,
            },
            "& .MuiDataGrid-columnHeader, & .MuiDataGrid-cell": {
              padding: "0 4px",
            },
            "& .MuiDataGrid-cell": {
              borderBottom: (theme) =>
                `1px solid ${
                  theme.palette.mode === "dark" ? "#29434e" : "#e0e0e0"
                }`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              lineHeight: 1.1,
            },
            "& .MuiDataGrid-row": {
              alignItems: "center !important",
            },
            "& .MuiDataGrid-columnHeaderTitle": {
              width: "100%",
              textAlign: "center",
              lineHeight: 1.2,
            },
          }}
          getRowId={(row) => row.id}
        />
        {error && !loading && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              backgroundColor: (theme) =>
                theme.palette.mode === "dark"
                  ? "rgba(0,0,0,0.4)"
                  : "rgba(255,255,255,0.6)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 14,
              fontWeight: 500,
              color: "error.main",
            }}
          >
            {error}
          </Box>
        )}
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={hideSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={hideSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Paper>
  );
}

export default function ObjectDetails() {
  const { t } = useTranslation();
  const [widgetData, setWidgetData] = useState(null);
  const [loading, setLoading] = useState(true);
  const location = useLocation();
  const rawId = getPlaceIdFromPathname(location.pathname);
  const nodeId = rawId ? parseInt(rawId, 10) : null;

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [startEpoch, setStartEpoch] = useState(null);
  const [endEpoch, setEndEpoch] = useState(null);
  const [selectedTimePeriod, setSelectedTimePeriod] = useState(7); // 7 = Bugün (default)

  const objectData = useObjectData();

  useEffect(() => {
    async function fetchWidgets() {
      setLoading(true);
      try {
        const projectCode = localStorage.getItem("projectCode");
        const projectData = await getProjects();
        const currentProject = projectData.find(
          (project) => project.code === projectCode
        );

        if (currentProject && currentProject.projectDetailWidgetJson) {
          const widgets = JSON.parse(currentProject.projectDetailWidgetJson);
          setWidgetData(widgets);
        } else {
          console.error("No widgets found for the current project.");
          setWidgetData({ widgets: [] });
        }
      } catch (error) {
        console.error("Error fetching widgets:", error);
        setWidgetData({ widgets: [] });
      } finally {
        setLoading(false);
      }
    }
    fetchWidgets();
  }, []);

  const hasWidgets =
    widgetData &&
    Array.isArray(widgetData.widgets) &&
    widgetData.widgets.length > 0;

  const handleApplyFilter = () => {
    if (startDate) {
      const startTimestamp = Math.floor(new Date(startDate).getTime() / 1000);
      setStartEpoch(startTimestamp);
    } else {
      setStartEpoch(null);
    }

    if (endDate) {
      const endTimestamp = Math.floor(new Date(endDate).getTime() / 1000);
      setEndEpoch(endTimestamp);
    } else {
      setEndEpoch(null);
    }

    setSelectedTimePeriod(null);
  };

  const handleQuickFilter = (timePeriod) => {
    setSelectedTimePeriod(timePeriod);
    setStartDate("");
    setEndDate("");
    setStartEpoch(null);
    setEndEpoch(null);
  };

  const handleClearFilter = () => {
    setStartDate("");
    setEndDate("");
    setStartEpoch(null);
    setEndEpoch(null);
    setSelectedTimePeriod(7); // Bugün filtresine dön
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      {hasWidgets && (
        <Box>
          <Paper
            elevation={3}
            sx={(theme) => ({
              p: 2,
              mb: 3,
              borderRadius: 2,
              backgroundColor: theme.palette.background.paper,
              border: `1px solid ${theme.palette.divider}`,
            })}
          >
            {/* Mobile Layout */}
            <Stack
              spacing={2}
              sx={{
                width: '100%',
                display: { xs: 'flex', md: 'none' }
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  color: 'text.primary',
                  textAlign: 'center',
                }}
              >
                {t("widgets.dateFilter", "Tarih Filtresi")}
              </Typography>

              <Stack direction="column" spacing={1}>
                <Button
                  variant={selectedTimePeriod === 7 ? "contained" : "outlined"}
                  onClick={() => handleQuickFilter(7)}
                  size="small"
                  sx={{
                    width: '100%',
                    borderRadius: 1,
                    textTransform: 'none',
                    fontWeight: 500,
                  }}
                >
                  {t("widgets.today", "Bugün")}
                </Button>
                <Button
                  variant={selectedTimePeriod === 6 ? "contained" : "outlined"}
                  onClick={() => handleQuickFilter(6)}
                  size="small"
                  sx={{
                    width: '100%',
                    borderRadius: 1,
                    textTransform: 'none',
                    fontWeight: 500,
                  }}
                >
                  {t("widgets.yesterday", "Dün")}
                </Button>
                <Button
                  variant={selectedTimePeriod === 5 ? "contained" : "outlined"}
                  onClick={() => handleQuickFilter(5)}
                  size="small"
                  sx={{
                    width: '100%',
                    borderRadius: 1,
                    textTransform: 'none',
                    fontWeight: 500,
                  }}
                >
                  {t("widgets.thisWeek", "Bu Hafta")}
                </Button>
                <Button
                  variant={selectedTimePeriod === 4 ? "contained" : "outlined"}
                  onClick={() => handleQuickFilter(4)}
                  size="small"
                  sx={{
                    width: '100%',
                    borderRadius: 1,
                    textTransform: 'none',
                    fontWeight: 500,
                  }}
                >
                  {t("widgets.lastWeek", "Geçen Hafta")}
                </Button>
              </Stack>

              <Box sx={{
                borderTop: (theme) => `2px solid ${theme.palette.divider}`,
              }} />

              <Stack direction="column" spacing={1}>
                <TextField
                  label={t("widgets.startDate", "Başlangıç")}
                  type="datetime-local"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  size="small"
                  sx={{
                    width: '100%',
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 1,
                    }
                  }}
                />
                <TextField
                  label={t("widgets.endDate", "Bitiş")}
                  type="datetime-local"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  size="small"
                  sx={{
                    width: '100%',
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 1,
                    }
                  }}
                />
                <Button
                  variant="contained"
                  onClick={handleApplyFilter}
                  size="small"
                  sx={{
                    width: '100%',
                    borderRadius: 1,
                    textTransform: 'none',
                    fontWeight: 500,
                  }}
                >
                  {t("widgets.apply", "Uygula")}
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleClearFilter}
                  size="small"
                  sx={{
                    width: '100%',
                    borderRadius: 1,
                    textTransform: 'none',
                    fontWeight: 500,
                  }}
                >
                  {t("widgets.clear", "Temizle")}
                </Button>
              </Stack>
            </Stack>

            {/* Desktop Layout */}
            <Stack
              direction="row"
              spacing={2}
              alignItems="center"
              sx={{
                width: '100%',
                justifyContent: 'space-between',
                display: { xs: 'none', md: 'flex' }
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  color: 'text.primary',
                  whiteSpace: 'nowrap',
                }}
              >
                {t("widgets.dateFilter", "Tarih Filtresi")}
              </Typography>

              <Box sx={{
                borderLeft: (theme) => `2px solid ${theme.palette.divider}`,
                height: 40,
              }} />

              <Stack direction="row" spacing={1} sx={{ flex: 1, justifyContent: 'center' }}>
                <Button
                  variant={selectedTimePeriod === 7 ? "contained" : "outlined"}
                  onClick={() => handleQuickFilter(7)}
                  size="small"
                  sx={{
                    minWidth: 80,
                    borderRadius: 1,
                    textTransform: 'none',
                    fontWeight: 500,
                  }}
                >
                  {t("widgets.today", "Bugün")}
                </Button>
                <Button
                  variant={selectedTimePeriod === 6 ? "contained" : "outlined"}
                  onClick={() => handleQuickFilter(6)}
                  size="small"
                  sx={{
                    minWidth: 80,
                    borderRadius: 1,
                    textTransform: 'none',
                    fontWeight: 500,
                  }}
                >
                  {t("widgets.yesterday", "Dün")}
                </Button>
                <Button
                  variant={selectedTimePeriod === 5 ? "contained" : "outlined"}
                  onClick={() => handleQuickFilter(5)}
                  size="small"
                  sx={{
                    minWidth: 90,
                    borderRadius: 1,
                    textTransform: 'none',
                    fontWeight: 500,
                  }}
                >
                  {t("widgets.thisWeek", "Bu Hafta")}
                </Button>
                <Button
                  variant={selectedTimePeriod === 4 ? "contained" : "outlined"}
                  onClick={() => handleQuickFilter(4)}
                  size="small"
                  sx={{
                    minWidth: 100,
                    borderRadius: 1,
                    textTransform: 'none',
                    fontWeight: 500,
                  }}
                >
                  {t("widgets.lastWeek", "Geçen Hafta")}
                </Button>
              </Stack>

              <Box sx={{
                borderLeft: (theme) => `2px solid ${theme.palette.divider}`,
                height: 40,
              }} />

              <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
                <TextField
                  label={t("widgets.startDate", "Başlangıç")}
                  type="datetime-local"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  size="small"
                  sx={{
                    width: 200,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 1,
                    }
                  }}
                />
                <TextField
                  label={t("widgets.endDate", "Bitiş")}
                  type="datetime-local"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  size="small"
                  sx={{
                    width: 200,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 1,
                    }
                  }}
                />
                <Button
                  variant="contained"
                  onClick={handleApplyFilter}
                  size="small"
                  sx={{
                    minWidth: 80,
                    borderRadius: 1,
                    textTransform: 'none',
                    fontWeight: 500,
                  }}
                >
                  {t("widgets.apply", "Uygula")}
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleClearFilter}
                  size="small"
                  sx={{
                    minWidth: 80,
                    borderRadius: 1,
                    textTransform: 'none',
                    fontWeight: 500,
                  }}
                >
                  {t("widgets.clear", "Temizle")}
                </Button>
              </Stack>
            </Stack>
          </Paper>

          {widgetData.widgets.map((row, rowIndex) => (
            <Grid container spacing={{ xs: 1, sm: 2 }} key={`row-${rowIndex}`}>
              {row.map((widget, colIndex) => (
                <Grid
                  item
                  xs={12}
                  sm={12 / row.length}
                  key={`widget-${rowIndex}-${colIndex}`}
                >
                  {widget.widgetType === "list" && (
                    <WidgetList
                      widget={widget}
                      getAggregation={getAggregation}
                      objectData={objectData}
                      nodeId={nodeId}
                      startEpoch={startEpoch}
                      endEpoch={endEpoch}
                      selectedTimePeriod={selectedTimePeriod}
                    />
                  )}
                </Grid>
              ))}
            </Grid>
          ))}
        </Box>
      )}
    </>
  );
}
